import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, NEVER, Observable, Subject, Subscription, catchError, debounceTime, filter, firstValueFrom, of, switchMap, take, tap, throwError } from 'rxjs';
import { AuthenticationService, TokenDecoded } from '../services/authentication.service';
import { OrganizationAccessService } from '../services/organization-access.service';
import { ActivatedRoute, Router } from '@angular/router';
import jwt_decode from "jwt-decode";
import moment from 'moment';

interface HTTPErrorDTO {
  status : number;
  error: boolean;
  message: ErrorIdentifiers;
}

enum ErrorIdentifiers {
  USER_SUSPENDED = "USER_SUSPENDED",
  ACCESS_ENDED = "ACCESS_ENDED",
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {

  private refreshing = false;
  private extending = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );


  private readonly extendSubject = new Subject<void>();
  private extendSubscription?: Subscription;

  constructor(
    private authService: AuthenticationService,
    private organizationAccessService: OrganizationAccessService,
    private router: Router
  ) {

    this.extendSubscription = this.extendSubject
      .pipe(
        debounceTime(2000)
      ).subscribe(async () => {
        if (this.authService.isLoggedIn())
          await this.extendAccessToken();
      });

  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.authService.getAccessToken() && !request.headers.has('sca')) {
      request = this.addAccessToken(request, this.authService.getAccessToken());
    }
    return next.handle(request).pipe(
      tap(async () => {
        if (!request.url.includes("auth/refreshToken")) {
          this.extendSubject.next();
        }
        await this.extendAccessTokenIfNeeded();
      }),
      catchError((error) => {
        // The organisation access of this session is over (on any route, the token renewal included): neither a
        // renewal nor a logout, but back to the platform organisation, which reloads the page. The request waits
        // for that reload instead of failing in front of the user. The logout is left alone: it must end the
        // local session whatever the backend answers.
        if (error instanceof HttpErrorResponse && error.status === 401 && (error.error as HTTPErrorDTO)?.message === ErrorIdentifiers.ACCESS_ENDED && !request.url.includes("auth/logout")) {
          this.organizationAccessService.ended();
          return NEVER;
        }
        if (error instanceof HttpErrorResponse && error.status === 401 && !request.headers.has('sca') && !["auth/login", "auth/refreshToken", "auth/logout", "auth/invitation", "auth/sso/"].some(path => request.url.includes(path))) {
          return this.handle401Error(request, next);
        } else if (error instanceof HttpErrorResponse && error.status === 403) {
          // When recieving a 403, there are two scenarios : being suspended or just not habing the correct rights
          // being suspended leads you to the login page
          if ((error.error as HTTPErrorDTO).message === ErrorIdentifiers.USER_SUSPENDED) {
            this.router.navigate(['/login']);
          }
        }
        return throwError(() => error);
      })
    );
  }

  private addAccessToken(request: HttpRequest<any>, token: string | null) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private async extendAccessTokenIfNeeded() {
    if (!this.extending) {

      const isAboutToExpire = this.authService.isAccessTokenAboutToExpire();
      if (isAboutToExpire) {
        this.extending = true;
        await firstValueFrom(this.authService.refreshToken());
        this.extending = false;
      }
    }
  }

  private async extendAccessToken() {
    if (!this.extending) {
      this.extending = true;
      await firstValueFrom(this.authService.refreshToken());
      this.extending = false;
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.refreshing) {
      this.refreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((token) => {
          this.refreshing = false;
          this.refreshTokenSubject.next(token);
          return next.handle(this.addAccessToken(request, token));
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return next.handle(this.addAccessToken(request, token));
        })
      );
    }
  }
}
